import { useState, useCallback, useMemo } from 'react';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qTreatmentsByEncounter,
  qTreatmentSuggestions,
  type PatientTreatmentEntryRow,
  type VttSuggestionRow,
} from '@helios/data-access';
import { ChipListField, type Chip } from './ChipListField';

/* ------------------------------------------------------------------ */
/*  Treatment plan entries (PRD.217)                                  */
/*                                                                    */
/*  Reference-only chip list backed by `patient_treatment_entries`.   */
/*  Per PRD.217 §16.5/16.6 these rows are NEVER promoted to           */
/*  prescriptions or billing items — those flows remain a deliberate  */
/*  clinician action via the Pharmacy and Billing tabs.               */
/*                                                                    */
/*  Chips with `source='ai'` (auto-added by a dermatology AI run)     */
/*  display an inline "AI Suggested" badge so origin is obvious.      */
/* ------------------------------------------------------------------ */

interface Props {
  patientId: string;
  encounterId: string;
  /** Brand primary color from the parent shell (kept for prop compatibility). */
  primary?: string;
  /** When true, hide the internal "Treatment Plan" header (used inside guided-note cards). */
  hideHeader?: boolean;
}

export function TreatmentPlanSection({ patientId, encounterId, hideHeader }: Props) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: entries = [] } = usePowerSyncQuery<PatientTreatmentEntryRow>(
    qTreatmentsByEncounter(encounterId),
  );

  const [search, setSearch] = useState('');
  const { data: suggestionRows = [] } = usePowerSyncQuery<VttSuggestionRow>(
    qTreatmentSuggestions(orgId, search),
  );
  const suggestions = useMemo(() => suggestionRows.map((r) => r.label), [suggestionRows]);

  const chips: Chip[] = useMemo(
    () =>
      entries.map((e) => ({
        id: e.id,
        label: e.label,
        badge: e.source === 'ai' ? 'AI Suggested' : undefined,
      })),
    [entries],
  );

  const handleAdd = useCallback(
    async (label: string) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_treatment_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [crypto.randomUUID(), orgId, null, patientId, encounterId, label, now, now],
      );
    },
    [db, orgId, patientId, encounterId],
  );

  const handleRemove = useCallback(
    async (chip: Chip) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_treatment_entries WHERE id = ?`, [chip.id]);
    },
    [db],
  );

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      {!hideHeader && (
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Treatment Plan
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Reference-only treatment items. Prescriptions and billed procedures live elsewhere.
          </p>
        </div>
      )}
      <ChipListField
        chips={chips}
        suggestions={suggestions}
        onSearchChange={setSearch}
        onAdd={handleAdd}
        onRemove={handleRemove}
        subtitle="Add or edit the treatment plan"
        addLabel="Add Treatment"
        placeholder="Add or edit treatment…"
      />
    </div>
  );
}
